import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import relativeTime from "dayjs/plugin/relativeTime.js";
import timezone from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc.js";
import pluralize from "pluralize";
import { assertIsDefined } from "shared/utils/utils";
import { monthDaySchema } from "shared/validation/monthDaySchema";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);

/**
 * @param input
 * @returns e.g. 2025
 */
export const formatYearDate = (value?: Date) =>
  dayjs(value || new Date())
    .utc()
    .format("YYYY");

/**
 * @param input
 * @returns e.g. 10/30/2021 in UTC timezone
 */
export const formatDate = (input: Date) => dayjs(input).utc().format("M/D/YYYY");

/**
 * @param input
 * @returns e.g. 10/14/1983, 9:30 AM EDT
 * @description Returns short datetime string with the local timezone of the user (when run on client) or the server (when run on server)
 */
export const formatDateTimeLocal = (input: Date) => dayjs(input).format("M/D/YYYY, h:mm A z");

/**
 * @param input
 * @returns e.g. "2 days ago"
 */
export function formatDateRelative(input: Date) {
  const dayjsInstance = dayjs(input).utc();
  const diffDays = dayjs().utc().startOf("day").diff(dayjsInstance, "days");
  const rtf1 = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (diffDays < 365) {
    return rtf1.format(-diffDays, "day");
  }
  const years = Math.round((diffDays + 0.5) / 365);
  return years === 1 ? "a year ago" : `${years} years ago`;
}

/**
 * @param input
 * @returns e.g. Oct 14, 1983, 9:30 AM
 * @description Returns med datetime string with local hour
 */
export const formatToMediumDateAndTime = (input: Date) =>
  dayjs(input).format("MMM D, YYYY, h:mm A");

/**
 * @param input
 * @returns e.g. 9:30 AM EDT
 * @description Returns short time string with the local timezone of the user (when run on client) or the server (when run on server)
 */
export const formatTimeLocal = (input: Date) => {
  return dayjs(input).format("h:mm A z");
};

/**
 * @param input
 * @returns e.g. 10/14/1983
 * @description Returns short date string with the local timezone of the user (when run on client) or the server (when run on server)
 */
export const formatShortDateTimeLocal = (input: Date) => dayjs(input).format("M/D/YYYY");

/**
 * @param input
 * @returns e.g. Oct 31
 */
export const formatDateShortMonth = (input: Date) => {
  return dayjs(input).utc().format("MMM D");
};

/**
 * @param input
 * @returns e.g. Wed Jan 5 2022 with the local timezone of the user
 */
export const formatDateWeekDayShortMonthWithYear = (input: Date) => {
  return dayjs(input).format("ddd MMM D YYYY");
};

/**
 * @param input
 * @returns e.g. Sat Jan 2, 2021, 7:00 AM EST
 */
export const formatDateWithTimeAndTimezone = (input: Date) => {
  return dayjs(input).format("ddd MMM D, YYYY, h:mm A z");
};

/**
 * @param input
 * @returns e.g. January 2
 */
export const formatDateFullMonth = (input: Date) => {
  return dayjs(input).utc().format("MMMM D");
};

/**
 * @param input
 * @returns e.g. October 1, 2021
 */
export const formatDateFullMonthWithYear = (input: Date) =>
  dayjs(input).utc().format("MMMM D, YYYY");

/**
 * @param input
 * @returns e.g. October 01, 2021
 */
export const formatDateLongFullMonthWithYear = (input: Date) =>
  dayjs(input).utc().format("MMMM DD, YYYY");

/**
 * @param input
 * @param includeYear
 * @returns e.g. "an October 31, 2021", "a September 31, 2021"
 */
export const formatDateFullMonthWithArticle = (input: Date, includeYear: boolean) => {
  const currentMonth = input.getMonth();
  // April, August, and October need "an" as an article
  const monthsForAnArticle = [2, 6, 8];
  const article = monthsForAnArticle.includes(currentMonth) ? "an" : "a";
  return `${article} ${
    includeYear ? formatDateFullMonthWithYear(input) : formatDateFullMonth(input)
  }`;
};

export const minutesToTimeFormat = (totalMinutes: number) => {
  if (totalMinutes <= 0) return "";

  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  const hours = pluralize("hour", h);
  const minutes = pluralize("minute", m);

  if (h && m) return `${h} ${hours} and ${m} ${minutes}`;
  if (h) return `${h} ${hours}`;
  if (m) return `${m} ${minutes}`;
  return "";
};

export const formatDateRangeUI = (start: Date, end: Date) => {
  const s = dayjs(start).utc();
  const e = dayjs(end).utc();
  const uiDateWithYear = "MMMM D, YYYY";
  const uiDateFormat = "MMMM D";
  if (s.year() !== e.year()) {
    return s.format(uiDateWithYear) + " - " + e.format(uiDateWithYear);
  } else if (s.month() !== e.month()) {
    return s.format(uiDateFormat) + " - " + e.format(uiDateFormat) + ", " + e.year();
  } else {
    return s.format(uiDateFormat) + " - " + e.date() + ", " + e.year();
  }
};

/**
 * @param input
 * @returns e.g. { date: 'Oct 1', time: '10AM EST' }
 */
export const formatRoundedDateAndTime = (input: Date) => {
  const datetime = dayjs(input).utc();
  const roundedDateTime =
    datetime.minute() > 30
      ? datetime.add(60 - datetime.minute(), "minute")
      : datetime.subtract(datetime.minute(), "minute");

  const date = roundedDateTime.tz("America/New_York").format("MMM D");
  const time = roundedDateTime.tz("America/New_York").format("hA [EST]");

  return {
    date,
    time,
  };
};

/**
 * @param input
 * @returns e.g. Oct-1-2021 13-07-01, Oct-1-2021 06-30-59
 */
export const formatDateTimeWithoutSlash = (input: Date): string => {
  return dayjs(input).tz("America/New_York").format("MMM-D-YYYY HH-mm-ss");
};

/**
 * Returns the date in MM/DD format
 * @returns e.g. 03/25
 */
export const formatDateToMMDD = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
  });
};

/**
 * Returns the date in YYYY-MM-DD format
 * @returns e.g. 2025-03-25
 */
export const formatDateOnlyToISOString = (date: Date): string => {
  return dayjs(date).format("YYYY-MM-DD");
};

/**
 * @param monthDay 12/31
 * @returns December 31st
 */
export function formatMonthDayToMonthOrdinal(monthDay: string): string;
export function formatMonthDayToMonthOrdinal(monthDay: `${number}/${number}`): string;
export function formatMonthDayToMonthOrdinal(monthDay: string) {
  const monthDayRegex = /^(?<month>\d{1,2})\/(?<day>\d{1,2})$/;
  if (!monthDayRegex.test(monthDay)) {
    throw new TypeError(
      `Argument must be in the format "MM/DD" (e.g. 12/31). Provided: ${monthDay}`,
    );
  }
  monthDaySchema.validateSync(monthDay);

  const [monthStr, dayStr] = monthDay.split("/");
  assertIsDefined(monthStr, "monthStr");
  assertIsDefined(dayStr, "dayStr");
  const month = Number.parseInt(monthStr, 10);
  const day = Number.parseInt(dayStr, 10);

  const pluralRules = new Intl.PluralRules("en-US", { type: "ordinal" });
  const suffixes: Record<Intl.LDMLPluralRule, string> = {
    zero: "",
    one: "st",
    two: "nd",
    few: "rd",
    many: "",
    other: "th",
  };
  const rule = pluralRules.select(day);
  const suffix = suffixes[rule];

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
  });
  const REFERENCE_YEAR = 2024; // 2024 is a leap year so it will allow Feb 29
  const date = new Date(REFERENCE_YEAR, month - 1, day);
  const formattedDate = dateFormatter.format(date) + suffix;
  return formattedDate;
}

/**
 * @param ms
 * @returns e.g. 5:00s
 */
export function formatDuration(ms: number) {
  const totalMinutes = Math.floor(ms / 60000); // Convert milliseconds to total minutes
  const seconds = Math.floor((ms % 60000) / 1000); // Get remaining seconds
  const formattedDuration = `${totalMinutes}:${seconds.toString().padStart(2, "0")}s`;
  return formattedDuration;
}

type KnownEntity = {
  firstName?: string | null;
  lastName?: string | null;
};

export const formatFullName = (entity: KnownEntity | undefined): string =>
  entity ? `${entity.firstName?.trim() ?? ""} ${entity.lastName?.trim() ?? ""}`.trim() : "";

export const extractFullName = (fullName: string): { firstName: string; lastName: string } => {
  const [firstName, ...lastName] = fullName.split(" ");
  return {
    firstName: firstName ?? "",
    lastName: lastName.join(" "),
  };
};

export type ListFormatOptions = {
  type: "conjunction" | "disjunction" | "unit";
  style: "long" | "short" | "narrow";
};

const defaultListFormat: ListFormatOptions = {
  type: "conjunction",
  style: "long",
};

export function listFormat(wordArray: string[], options: ListFormatOptions = defaultListFormat) {
  const list: string = new Intl.ListFormat("en-US", options).format(wordArray);
  return list;
}
